import {NgClass} from '@angular/common';
import {Component} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {NgxMaskDirective} from 'ngx-mask';
import {ButtonDirective} from 'primeng/button';
import {DividerModule} from 'primeng/divider';
import {InputTextModule} from 'primeng/inputtext';
import {Ripple} from 'primeng/ripple';
import {first, tap} from 'rxjs';
import {AuthService} from '../../../services/auth.service';
import {AuthBackgroundComponent} from '../auth-background/auth-background.component';

interface MultifactorAuthenticationForm {
  code: FormControl<string | null>;
}

@UntilDestroy()
@Component({
  selector: 'em-multifactor-auth-page',
  templateUrl: './multifactor-auth-page.component.html',
  imports: [
    AuthBackgroundComponent,
    NgClass,
    ReactiveFormsModule,
    ButtonDirective,
    DividerModule,
    NgxMaskDirective,
    InputTextModule,
    Ripple,
  ],
})
export class MultifactorAuthPageComponent {
  private email: string;
  private password: string;

  phoneLastTwoDigits: string;
  form: FormGroup<MultifactorAuthenticationForm>;
  isButtonLoading = false;
  isError = false;
  fadeOut = false;
  showResendButton = true;

  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly fb: FormBuilder,
  ) {
    const state = this.router.getCurrentNavigation()?.extras?.state;
    const email = state?.['email'];
    const password = state?.['password'];
    const phoneLastTwoDigits = state?.['phoneLastTwoDigits'];
    if (!email || !phoneLastTwoDigits || !password) {
      this.router.navigate(['/', 'auth']);
    }
    this.email = email;
    this.password = password;
    this.phoneLastTwoDigits = phoneLastTwoDigits;
    this.form = this.createForm();
  }

  submit(): void {
    this.isButtonLoading = true;
    this.authService
      .validateMultifactorAuthenticationCode(this.email, this.password, this.form.controls.code.value!)
      .pipe(first())
      .subscribe({
        next: (result) => {
          this.isError = false;
          this.fadeOut = true;
          this.authService.handleSuccessfulLoginRedirection(result.session);
        },
        error: () => {
          this.isButtonLoading = false;
          this.isError = true;
        },
      });
  }

  resendCode(): void {
    this.showResendButton = false;
    this.authService
      .resendMultifactorAuthenticationCode(this.email)
      .pipe(
        first(),
        tap(() => {
          this.isError = false;
        }),
      )
      .subscribe();
  }

  private createForm(): FormGroup<MultifactorAuthenticationForm> {
    const form = this.fb.group<MultifactorAuthenticationForm>({
      code: this.fb.control(null, [Validators.required, Validators.minLength(6), Validators.maxLength(6)]),
    });

    form.controls.code.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.isError = false;
    });

    return form;
  }
}
